'use client';

import { Button } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { LivePulseDot } from '@repo/design-system/components/ui/live-pulse-dot';
import { Type } from '@repo/design-system/components/ui/type';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { useGetMapHeatmapQuery } from '@repo/store/maps/api/mapsApi';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectIsMobileViewport } from '@repo/store/maps/slices/clientContext';
import {
    selectExternalMapId,
    selectHeatmapEventVisibility,
    selectTitleId,
    selectTitleSlug,
    toggleAllHeatmapEventsVisibility,
    toggleHeatmapEventVisibility,
} from '@repo/store/maps/slices/mapData';
import React, { useMemo, useState } from 'react';
import { resolveHeatmapTitleConfig } from '../game-map/data/heatmap-config';
import {
    getHeatmapEventCount,
    getHeatmapEventOptions,
    HEATMAP_EVENTS_CATEGORY_COLOR,
} from '../game-map/data/heatmap-events';
import { getHeatmapEventCategoryLabelSelector } from '../game-map/data/heatmap-i18n';

const subcategoriesStyles = css({
    display: 'grid',
    gap: '4',
    width: 'full',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(120px, 100%), 1fr))',
    _compact: {
        gridTemplateColumns: '1fr',
    },
});

export interface HeatmapEventsSectionProps {
    searchTerm?: string;
}

export const HeatmapEventsSection: React.FC<HeatmapEventsSectionProps> = React.memo(({ searchTerm = '' }) => {
    const dispatch = useAppDispatch();
    const { t } = useT(['game_map']);
    const [isExpanded, setIsExpanded] = useState(true);
    const isMobileViewport = useAppSelector(selectIsMobileViewport);
    const titleSlug = useAppSelector(selectTitleSlug);
    const titleId = useAppSelector(selectTitleId);
    const mapExternalId = useAppSelector(selectExternalMapId);
    const visibility = useAppSelector(selectHeatmapEventVisibility);

    const titleConfig = useMemo(
        () => resolveHeatmapTitleConfig({ titleSlug, titleId, mapExternalId }),
        [mapExternalId, titleId, titleSlug],
    );

    const { data: heatmapResponse } = useGetMapHeatmapQuery(
        {
            titleId: titleConfig?.titleId ?? 0,
            eventTypes: titleConfig ? [...titleConfig.eventTypes] : [],
            days: titleConfig?.days ?? 14,
        },
        { skip: !titleConfig },
    );

    const options = useMemo(() => getHeatmapEventOptions(heatmapResponse, titleConfig), [heatmapResponse, titleConfig]);

    const categoryTitle = t($ => $.heatmap_events.category_title);

    const searchQuery = searchTerm.trim().toLowerCase();
    const filteredOptions = !searchQuery
        ? options
        : options.filter(option => {
              const selector = getHeatmapEventCategoryLabelSelector(option.eventType);
              const label = (selector ? t(selector) : option.eventType).toLowerCase();
              return label.includes(searchQuery) || categoryTitle.toLowerCase().includes(searchQuery);
          });

    if (!heatmapResponse || filteredOptions.length === 0) {
        return null;
    }

    const allHidden = options.every(option => visibility[option.eventType] === false);

    const handleCategoryToggle = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dispatch(toggleAllHeatmapEventsVisibility(options.map(option => option.eventType)));
    };

    const handleEventToggle = (eventType: string) => {
        dispatch(toggleHeatmapEventVisibility(eventType));
    };

    return (
        <FlexColumn
            width="full"
            className={css.raw({
                contentVisibility: 'auto',
                containIntrinsicSize: '[auto 80px]',
            })}
        >
            <FlexRow width="full" align="center" gap="12">
                <Button
                    variant="ghost"
                    aria-expanded={isExpanded}
                    onClick={() => setIsExpanded(expanded => !expanded)}
                    color="primary"
                    size="sm"
                    radius="rounded"
                    className={css({
                        justifyContent: 'start',
                        flexGrow: 1,
                        _active: { transform: 'scale(0.97)', opacity: '0.8' },
                    })}
                >
                    <Icon
                        icon={isExpanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right'}
                        decorative
                        className={css({ marginLeft: '-4' })}
                    />
                    <FlexRow align="center" gap="8">
                        <Type variant="caption" style={{ opacity: allHidden ? 0.5 : 1 }}>
                            {categoryTitle}
                        </Type>
                        <LivePulseDot size={8} />
                    </FlexRow>
                </Button>

                <Button
                    variant="ghost"
                    onClick={handleCategoryToggle}
                    color="primary"
                    size="sm"
                    radius="rounded"
                    title={allHidden ? t($ => $.heatmap_events.show_category) : t($ => $.heatmap_events.hide_category)}
                    className={css({
                        minWidth: '[36px]',
                        minHeight: '[36px]',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0',
                        _active: { transform: 'scale(0.97)', opacity: '0.8' },
                    })}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '4px',
                            minWidth: '16px',
                            minHeight: '16px',
                            width: '16px',
                            height: '16px',
                            backgroundColor: HEATMAP_EVENTS_CATEGORY_COLOR,
                            position: 'relative',
                        }}
                        className={css({
                            '&:focus-within': {
                                filter: '[brightness(0.8)]',
                            },
                            borderColor: 'whiteAlpha.30',
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            borderRadius: '4',
                        })}
                    >
                        {!allHidden && (
                            <Icon className={css({ position: 'absolute' })} fill icon="check" size="md" decorative />
                        )}
                    </div>
                </Button>
            </FlexRow>

            {isExpanded && (
                <div className={subcategoriesStyles}>
                    {filteredOptions.map(option => {
                        const isHidden = visibility[option.eventType] === false;
                        const count = getHeatmapEventCount(heatmapResponse, option.eventType);
                        const selector = getHeatmapEventCategoryLabelSelector(option.eventType);
                        const label = selector ? t(selector) : option.eventType;

                        return (
                            <Button
                                key={option.eventType}
                                variant="ghost"
                                onClick={() => handleEventToggle(option.eventType)}
                                color="primary"
                                size="sm"
                                radius="rounded"
                                fullWidth
                                title={label}
                                className={css({
                                    opacity: isHidden ? '0.35' : '1',
                                    _active: { transform: 'scale(0.97)', opacity: '0.8' },
                                    ...(isMobileViewport ? { minHeight: '[44px]' } : {}),
                                })}
                            >
                                <Icon
                                    icon={option.icon}
                                    fill
                                    className={css({
                                        color: 'whiteAlpha.70',
                                        flexShrink: '0',
                                    })}
                                    decorative
                                />
                                <Type color="whiteAlpha.80" variant="body-xs" truncate>
                                    {label}
                                </Type>
                                <Type className={css({ marginLeft: 'auto' })} color="whiteAlpha.80" variant="caption">
                                    {count.toLocaleString()}
                                </Type>
                            </Button>
                        );
                    })}
                </div>
            )}
        </FlexColumn>
    );
});

HeatmapEventsSection.displayName = 'HeatmapEventsSection';
